import { AUTO_DESIGN_MAX_COLORS, DEFAULT_AUTO_DESIGN_COLOR } from '../../constants/autoDesign'
import { FcGeneratorOptions } from '@dtx-company/flowcode-generator-browser/src'
import { FlowcodeDownloadOptionMimeType } from '@dtx-company/inter-app/src/types/flowcode'
import { OptimizeVariant } from '../../types/optimizely'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface AutoDesignedCodeBatch {
  shortUrl: string
  batchId: string
  directoryId?: number
  flowcodeCount?: number
  waitedForCodes?: boolean
}

interface CombinedFCOptionBatchType {
  codeOptions: FcGeneratorOptions | null
  codeBatchId: string | null
}
export interface HomePageState {
  shouldCreateTransparentCode: boolean
  fileTypeToDownload: FlowcodeDownloadOptionMimeType
  autoDesignColorsChosen: string[]
  centerImageFile: {
    name: string
    height: number
    width: number
    objectUrl: string
  }
  autoDesignedCodeOptions: FcGeneratorOptions | null
  autoDesignedCodeBatch: AutoDesignedCodeBatch | null
  showStaticContent: boolean
  showProUpsellExperiment: boolean
  hasUserDismissedProUpsellBanner: boolean
  userHasClickedDownloadCodeButton: boolean
  showMobileSignupStep: boolean
  proUpsellTopNavBannerExperimentData: {
    variant: string
  }
  homepageVariant: OptimizeVariant
  optimizeLoaded: boolean
  showEmailSuccess: boolean
  downloadCodeData: CombinedFCOptionBatchType
}

const initialState: HomePageState = {
  shouldCreateTransparentCode: false,
  fileTypeToDownload: FlowcodeDownloadOptionMimeType.PNG,
  autoDesignColorsChosen: ['#000000'],
  centerImageFile: {
    name: '',
    height: 0,
    width: 0,
    objectUrl: ''
  },
  autoDesignedCodeOptions: null,
  autoDesignedCodeBatch: null,
  showStaticContent: true,
  showProUpsellExperiment: false,
  hasUserDismissedProUpsellBanner: false,
  userHasClickedDownloadCodeButton: false,
  showMobileSignupStep: false,
  proUpsellTopNavBannerExperimentData: {
    variant: '0'
  },
  homepageVariant: OptimizeVariant.ORIGINAL,
  optimizeLoaded: false,
  showEmailSuccess: false,
  downloadCodeData: {
    codeOptions: null,
    codeBatchId: null
  }
}

const homePageSlice = createSlice({
  name: 'homePage',
  initialState,
  reducers: {
    chooseAutoDesignColor: (state, action: PayloadAction<string>) => {
      const colorToAdd = action.payload
      const maxColorsChosen = state.autoDesignColorsChosen.length === AUTO_DESIGN_MAX_COLORS
      const colorAlreadyChosen = state.autoDesignColorsChosen.includes(colorToAdd)
      if (maxColorsChosen || colorAlreadyChosen) return
      state.autoDesignColorsChosen = [...state.autoDesignColorsChosen, colorToAdd]
    },
    removeAutoDesignColor: (state, action: PayloadAction<string>) => {
      const colorToRemove = action.payload
      state.autoDesignColorsChosen = state.autoDesignColorsChosen.filter(
        color => color !== colorToRemove
      )
    },
    updateCenterImage: (
      state,
      action: PayloadAction<{ name: string; height: number; width: number; objectUrl: string }>
    ) => {
      state.centerImageFile = action.payload
    },
    clearCenterImage: state => {
      state.centerImageFile = {
        name: '',
        height: 0,
        width: 0,
        objectUrl: ''
      }
    },
    updateAutoDesignedCodeOptions: (state, action: PayloadAction<FcGeneratorOptions | null>) => {
      state.autoDesignedCodeOptions = action.payload
    },
    updateBatch: (state, action: PayloadAction<AutoDesignedCodeBatch>) => {
      state.autoDesignedCodeBatch = action.payload
    },
    updateCenterImageInCodePreview: (
      state,
      action: PayloadAction<{ height: number; width: number; objectUrl: string }>
    ) => {
      if (state.autoDesignedCodeOptions) {
        state.autoDesignedCodeOptions.logoWidth = action.payload.width
        state.autoDesignedCodeOptions.logoHeight = action.payload.height
        state.autoDesignedCodeOptions.logoImageUrl = action.payload.objectUrl
        state.autoDesignedCodeOptions.logoFillSvgColor = DEFAULT_AUTO_DESIGN_COLOR
      }
    },
    updateDownloadCodeData: (state, action: PayloadAction<CombinedFCOptionBatchType>) => {
      state.downloadCodeData.codeOptions = action.payload.codeOptions
      state.downloadCodeData.codeBatchId = action.payload.codeBatchId
    },
    updateFileTypeToDownload: (state, action: PayloadAction<FlowcodeDownloadOptionMimeType>) => {
      state.fileTypeToDownload = action.payload
    },
    updateFlowcodeOptionsTransparency: (state, action: PayloadAction<boolean>) => {
      state.shouldCreateTransparentCode = action.payload
    },
    setShowStaticContent: (state, action: PayloadAction<boolean>) => {
      state.showStaticContent = action.payload
    },
    setShowProUpsellExperiment: (state, action: PayloadAction<boolean>) => {
      state.showProUpsellExperiment = action.payload
    },
    setHasUserDismissedProUpsellBanner: (state, action: PayloadAction<boolean>) => {
      state.hasUserDismissedProUpsellBanner = action.payload
    },
    recordUserClickedDownloadCodeButton: state => {
      state.userHasClickedDownloadCodeButton = true
    },
    setShowMobileSignupStep: (state, action: PayloadAction<boolean>) => {
      state.showMobileSignupStep = action.payload
    },
    setProUpsellTopNavBannerExperimentData: (state, action: PayloadAction<string>) => {
      state.proUpsellTopNavBannerExperimentData.variant = action.payload
    },
    setHomepageVariant(state, action: PayloadAction<OptimizeVariant>) {
      state.homepageVariant = action.payload
    },
    setOptimizeLoaded(state, action: PayloadAction<boolean>) {
      state.optimizeLoaded = action.payload
    },
    setShowEmailSuccess: (state, action: PayloadAction<boolean>) => {
      state.showEmailSuccess = action.payload
    }
  }
})

export const {
  chooseAutoDesignColor,
  removeAutoDesignColor,
  updateCenterImage,
  clearCenterImage,
  updateAutoDesignedCodeOptions,
  updateCenterImageInCodePreview,
  updateDownloadCodeData,
  updateFileTypeToDownload,
  updateFlowcodeOptionsTransparency,
  updateBatch,
  setShowStaticContent,
  setShowProUpsellExperiment,
  setHasUserDismissedProUpsellBanner,
  recordUserClickedDownloadCodeButton,
  setShowMobileSignupStep,
  setProUpsellTopNavBannerExperimentData,
  setHomepageVariant,
  setOptimizeLoaded,
  setShowEmailSuccess
} = homePageSlice.actions

export default homePageSlice.reducer
